// apps/pwa-pos/src/lib/payments.ts
// Sprint 37 Fase 2 — Tipos de métodos de pago y helpers para el checkout.
// El set de métodos es cerrado (D7 del sprint). Cuando crezca (Nequi, Pago
// Móvil, puntos, …) se migra a una tabla `metodos_pago`.

import { roundMoney } from '@mibodega/domain';

/**
 * Métodos de pago soportados en el POS.
 *
 * - EFECTIVO:      pago inmediato en moneda base o la del carrito.
 * - CREDITO:       fiado a cliente registrado. Marca `es_credito=true` y
 *                  `cliente_id` en la venta. Sólo 1 pago por venta (el monto
 *                  total). Genera cargo en `movimientos_credito`.
 * - DOLARES:       pago en USD convertido a moneda base con la tasa vigente
 *                  (de `orgConfig`). Guarda monto en USD + moneda base.
 * - TARJETA:       datáfono. Campo `referencia` (nro. aprobación) opcional.
 * - TRANSFERENCIA: pago por transferencia/banco. `referencia` opcional.
 * - MIXTO:         N pagos parciales (cualquier combinación de los anteriores
 *                  excepto CREDITO). Se insertan N filas en `pagos_venta`,
 *                  una por cada método específico. No se persiste MIXTO como
 *                  fila propia — es sólo un flag del UI.
 */
export type PagoMetodo = 'EFECTIVO' | 'CREDITO' | 'DOLARES' | 'TARJETA' | 'TRANSFERENCIA';

/** Métodos que admiten el campo `referencia` (datáfono/transfer). */
export const METODOS_CON_REFERENCIA: ReadonlyArray<PagoMetodo> = ['TARJETA', 'TRANSFERENCIA'];

/** Una fila que se va a persistir en `pagos_venta` (Fase 3). */
export interface PagoVenta {
  metodo: PagoMetodo;
  /** Código de moneda del pago (ej: 'COP', 'USD'). */
  monedaCodigo: string;
  /** Monto en la moneda del pago. */
  monto: number;
  /** Conversión a moneda_base — la calcula la RPC server-side al subir. */
  montoBase?: number;
  /** Tasa vs moneda_base al momento del cobro. */
  tasaCambio?: number;
  /** Nro. aprobación datáfono / referencia transferencia. Opcional. */
  referencia?: string;
}

const TOLERANCIA_CENTAVO = 0.01;

/**
 * Verifica si una lista de pagos cuadra exactamente con el total de la venta.
 * Tolerancia: 0.01 (1 centavo) para errores de redondeo.
 */
export function pagosCuadran(pagos: PagoVenta[], total: number): boolean {
  if (pagos.length === 0) return false;
  const suma = pagos.reduce((acc, p) => acc + (Number.isFinite(p.monto) ? p.monto : 0), 0);
  return Math.abs(roundMoney(suma) - roundMoney(total)) < TOLERANCIA_CENTAVO;
}

/** Suma de los pagos en su moneda (no en moneda_base). */
export function sumaPagos(pagos: PagoVenta[]): number {
  return roundMoney(pagos.reduce((acc, p) => acc + (p.monto || 0), 0));
}

/**
 * El cliente pagó al menos el total (permite vuelto; bloquea faltante).
 * Tolerancia 1 centavo por redondeo.
 */
export function pagoCubreTotal(monto: number, total: number): boolean {
  if (!Number.isFinite(monto) || !Number.isFinite(total) || monto <= 0) return false;
  return roundMoney(monto) + TOLERANCIA_CENTAVO >= roundMoney(total);
}

/**
 * Faltante a cobro (0 si ya cubre o supera el total).
 */
export function faltantePago(monto: number, total: number): number {
  if (!Number.isFinite(monto) || !Number.isFinite(total)) return roundMoney(total);
  const f = roundMoney(total - monto);
  return f > 0 ? f : 0;
}

/**
 * Cambio a devolver al cliente en pagos EFECTIVO en la misma moneda que el total.
 * Devuelve 0 si el pago es menor al total (no negativo — bloqueamos el cobrar).
 */
export function calcularCambio(pagoCon: number, total: number): number {
  const diff = roundMoney(pagoCon - total);
  return diff > 0 ? diff : 0;
}

/** Indica si una combinación de pagos es "mixto" (más de un método). */
export function esPagoMixto(pagos: PagoVenta[]): boolean {
  return pagos.length > 1;
}

/** Etiqueta humana para mostrar en UI. */
export function labelMetodo(m: PagoMetodo): string {
  switch (m) {
    case 'EFECTIVO':
      return 'Efectivo';
    case 'CREDITO':
      return 'Crédito';
    case 'DOLARES':
      return 'Dólares';
    case 'TARJETA':
      return 'Tarjeta';
    case 'TRANSFERENCIA':
      return 'Transferencia';
  }
}

/** Icono Lucide para cada método. El caller los importa por su cuenta. */
export type IconKey = 'Wallet' | 'UserCheck' | 'DollarSign' | 'CreditCard' | 'ArrowRightLeft';

export function iconForMetodo(m: PagoMetodo): IconKey {
  switch (m) {
    case 'EFECTIVO':
      return 'Wallet';
    case 'CREDITO':
      return 'UserCheck';
    case 'DOLARES':
      return 'DollarSign';
    case 'TARJETA':
      return 'CreditCard';
    case 'TRANSFERENCIA':
      return 'ArrowRightLeft';
  }
}
